import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  ApiJobListCards,
  ApiBreadcrumbs,
  buildSearchParams,
  fetcher,
  ApiBreadcrumbsOk,
  isJobListCardsOk,
  isBreadcrumbsOk,
  ObjectFilterParams,
  ApiOwnResumeDetail,
  isOwnResumeDetailOk,
  ApiOwnResumeDetailOk,
} from '@utils/data-fetching';
import {
  useEffect, ReactNode, useMemo, useState, useCallback,
} from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { bjToast, Button } from '@components/common';
import { BellIcon } from '@heroicons/react/24/solid';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from '@store/index';
import {
  paginatedServerSideQuery,
  PaginatedServerSideQueryResponse,
} from '@utils/infinite-scrolling/processedServerSideQueries';
import { JobSeoWidget } from '@components/templates/job/SeoWidget';
import { JobSearchFilters } from '@components/features/filters/JobSearchFilters';
import { useFiltersQueryParams } from '@hooks/filters/useFiltersQueryParams';
import { countAppliedFilters } from '@components/features/filters/helpers/countAppliedFilters';
import { clsx } from 'clsx';
import { useCreateJobSearchAlert } from '@hooks/features/useCreateJobSearchAlert';
import { JobListType } from '@type/job-params';
import { getJobBulkApplyFeed } from '@store/reducers/jobSelector';
import dynamic from 'next/dynamic';

import { Breadcrumbs } from '@components/templates/job/Breadcrumbs';
import { gsspCheckCookieAuth, gsspGetLocale } from '@utils/gssp';
import { JobCardList } from '@components/templates/job/Card/JobCardList';
import { getJobListMetaDescription, getJobListParametersFromSlug, getJobListTitle } from '@utils/job';
import { getCanonicalUrl } from '@services/i18nPaths';
import { useRouter } from 'next/router';
import { JobSearchQueryParams, FiltersQueryParamsStates } from '@type/filters';
import { LayoutDefault } from '@components/layout/Layouts/LayoutDefault';
import { getJobListHeadTitle } from '@utils/job/buildJobListHeadTitle';
import { useSwrJobListBreadcrumbs, useSwrJobListCardsInfinite } from '@hooks/useSwrApi/endpoints/job';
import { FiltersObjectInterface } from '@type/form-types';
import { JOB_LIST_ITEMS_PER_PAGE } from 'src/constants';
import { getServerSideTranslation } from '@app/i18n';
import ogImage from '@images/social-media/bestie.png';
import { useSwrJobSearchSeo } from '@root/src/hooks/useSwrApi/endpoints/job/useSwrJobSearchSeo';
import { FilterDrawerProvider, useFilterDrawer } from '@components/contexts/FilterDrawerContext';
import { useGeolocationPermission } from '@hooks/useGeolocationPermission';
import { useJobListLocationRestore } from '@hooks/useJobListLocationRestore';
import { getCachedCity, setCachedCity } from '@utils/location-city-cache/location-city-cache';
import { JobListBanners } from '@components/templates/job/list/JobListBanners';
import type { BulkApplyModalProps } from '@components/templates/job/apply/BulkApplyModal';

const JobAlertCard = dynamic(() => import('@components/templates/job/list/JobAlertCard').then((mod) => mod.JobAlertCard));

const googleAPIKey = process.env.GOOGLE_MAPS_CLIENT_API_KEY as string;

// Lazy load Google Maps APIProvider - only load when needed
// Create a wrapper component that tracks API readiness
const GoogleMapsProvider = dynamic(
  () => import('@vis.gl/react-google-maps').then((mod) => {
    const Wrapper = ({
      apiKey,
      language,
      onReady,
    }: {
      apiKey: string;
      language: string;
      onReady?: () => void;
    }) => {
      const MapsReadyTracker = () => {
        const isReady = mod.useApiIsLoaded();

        useEffect(() => {
          if (isReady) {
            onReady?.();
          }
        }, [isReady]);
        return null;
      };

      return (
        <mod.APIProvider apiKey={apiKey} language={language} key={`gmaps-${language}`}>
          <MapsReadyTracker />
        </mod.APIProvider>
      );
    };
    Wrapper.displayName = 'GoogleMapsProvider';
    return { default: Wrapper };
  }),
  { ssr: false },
);

// Job Alert banner positions (how many cards to be displayed before the banner)
const JOB_ALERT_BANNER_POSITIONS = [12, 24] as const;

// Interface
// ******************************************
interface JobListPageProps {
  locale: Locale,
  infiniteQueryParams: PaginatedServerSideQueryResponse,
  jobListCardsFromServer: ApiSchema<'JobResult'> | null,
  breadcrumbsFromServer: ApiBreadcrumbsOk | null,
  seoDataFromServer: ApiSchema<'SeoData'> | null,
  isAuthenticated: boolean,
  userOwnResumeFromServer: ApiOwnResumeDetailOk | null,
  ipCityFromHeader?: string | null,
}

// Variable that we can mutate in order to pass the query params to the search input component (keyword) via HeaderSearchContext
// ******************************************
let headerSearchContextValue: FiltersQueryParamsStates<JobSearchQueryParams>;

// Api keys that need to be an array. The thing is, form multiples (checkbox, select) return a string when only one value
// is selected, and array when multiple values are selected. The API expects arrays for these keys, even if only one value is selected.
// ******************************************
const apiKeysAsArray = ['domain', 'careerLevels', 'employmentTypes', 'spokenLanguages', 'benefits'];

/**
 * Parse x-client-citylatlong header to extract latitude and longitude
 * @param headerValue - The header value in format "lat,lon"
 * @returns Object with _lat and _lon or null if invalid
 */
const parseLocationHeader = (headerValue: string | undefined): { _lat: string; _lon: string } | null => {
  if (!headerValue) return null;
  const parts = headerValue.split(',');
  if (parts.length !== 2) return null;
  const [lat, lon] = parts.map((part) => part.trim());
  // Basic validation - check if they're numeric
  if (!lat || !lon || Number.isNaN(Number(lat)) || Number.isNaN(Number(lon))) return null;
  // Basic range validation for latitude and longitude
  const latNum = Number(lat);
  const lonNum = Number(lon);
  if (latNum < -90 || latNum > 90 || lonNum < -180 || lonNum > 180) return null;
  return { _lat: lat, _lon: lon };
};

/**
 * @description Job List page
 * - **locale**: string - the app locale.
 * - **infiniteQueryParams**: PaginatedServerSideQueryResponse - the object that contains the query params for the infinite scrolling.
 * - **jobListCardsFromServer**: ApiSchema<'JobResult'> | null - the job list data fetched from the server.
 * - **breadcrumbs**: ApiBreadcrumbsOk | null - the breadcrumb object fetched from the server.
 * - **isAuthenticated**: boolean - a boolean that indicates whether the user is authenticated.
 */
const JobListPage = (props: JobListPageProps) => {
  // Destructure props
  const {
    locale,
    infiniteQueryParams,
    jobListCardsFromServer,
    breadcrumbsFromServer,
    seoDataFromServer,
    isAuthenticated,
    userOwnResumeFromServer,
    ipCityFromHeader,
  } = props;

  // Destructure paginatedQuery
  const {
    cursor,
    limit,
    objFilterParams,
  } = infiniteQueryParams;

  // Translation
  const { t } = useTranslation('common');

  // Track if Google Maps should be loaded (lazy loading trigger)
  const [shouldLoadMaps, setShouldLoadMaps] = useState(false);
  const [isMapsReady, setIsMapsReady] = useState(false);

  // Handle query params in browser URL, history and filtering
  // ***********************************************
  const {
    queryParams,
    setQueryParams,
    setCurrentPage,
  } = useFiltersQueryParams({
    serverQueryParams: objFilterParams,
    initialPage: cursor,
    source: 'jobList',
    t,
    paginationType: 'cursor',
  }) as FiltersQueryParamsStates<JobSearchQueryParams> & { setCurrentPage: Dispatch<SetStateAction<string | number>> };

  // Next router
  const { asPath } = useRouter();

  // Fetch data
  const {
    data,
    size,
    setSize,
    isLoading,
    isEmpty,
    sizeAsRef,
    isValidating,
    showLoadMore,
  } = useSwrJobListCardsInfinite({
    locale,
    limit,
    serverSideData: jobListCardsFromServer ?? undefined,
    initialCursor: cursor,
    // Pass the query params to the hook
    urlParams: buildSearchParams(queryParams, '&', false, apiKeysAsArray),
  });

  // Hook: capture/restore coords and force refetch when coords become available
  const { rememberLastCoords } = useJobListLocationRestore({
    queryParams,
    setQueryParams,
    setCurrentPage,
    setSize,
  });

  const lastGroup = (data && data.length) ? data[data.length - 1] : undefined;
  const apiRequestId = lastGroup?.apiRequestId;

  const { data: seoData } = useSwrJobSearchSeo({
    locale,
    serverSideData: seoDataFromServer,
    urlParams: buildSearchParams(queryParams, '?', false, apiKeysAsArray),
  });

  // SWR: Fetch breadcrumbs
  // ***********************************************
  const { data: breadcrumbs } = useSwrJobListBreadcrumbs({
    locale,
    shouldFetch: !isAuthenticated,
    urlParams: buildSearchParams(queryParams, '?', false, apiKeysAsArray),
    serverSideData: breadcrumbsFromServer ?? undefined,
  });

  // Data for Header Search Context
  // ***********************************************
  headerSearchContextValue = useMemo(() => ({
    queryParams,
    setQueryParams,
  }), [queryParams, setQueryParams]);

  // Reset context value when leaving the page - because the headerSearchContextValue is outside the component
  // It's not cleaned when doing client navigation - requires a hard refresh for cleanup
  useEffect(() => () => {
    headerSearchContextValue = {
      queryParams: {},
      setQueryParams: () => {},
    };
  }, []);

  // Helpers
  // ***********************************************
  const firstGroup = (data && data.length) ? data[0] : undefined;
  const searchId = firstGroup?.searchId;
  const searchAlertExists = firstGroup?.jobSearchAlertExists;

  const locArr = (queryParams?.location ?? undefined) as unknown as string[] | undefined;
  const hasManualLocation = Array.isArray(locArr) && locArr.length > 0;

  const showLoadingOverlay = useMemo(
    () => isValidating && !isLoading,
    [isValidating, isLoading],
  );

  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Filter labels state
  // ***********************************************
  const [filterLabels, setFilterLabels] = useState<FiltersObjectInterface | undefined>(undefined);

  // Metadata
  // *****************************************************
  const title = getJobListTitle(t, filterLabels);
  const headTitle = getJobListHeadTitle(t, locale, title);
  const metaDescription = getJobListMetaDescription(t, filterLabels);

  const ogUrl = getCanonicalUrl(asPath);
  const facebookAppId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID ?? '';
  const ogImageUrl = ogImage.src.startsWith('http') ? ogImage.src : `${process.env.NEXT_PUBLIC_BASE_URL}${ogImage.src}`;

  // Create job search alert hook
  // *****************************************************
  const {
    createJobSearchAlert,
    isCreatingJobSearchAlert,
    isSavedJobSearchAlert,
  } = useCreateJobSearchAlert({
    searchId: searchId || '',
    alertExists: !!searchAlertExists,
  });

  // Bulk Apply Feed (from REDUX)
  // *****************************************
  const bulkApplyFeed = useAppSelector(getJobBulkApplyFeed);

  useEffect(() => {
    if (bulkApplyFeed?.length) {
      void (async () => {
        const { showBulkApplyModal } = await import('@components/templates/job/apply/BulkApplyModal');
        await showBulkApplyModal({ jobList: bulkApplyFeed, locale } satisfies BulkApplyModalProps);
      })();
    }
  }, [bulkApplyFeed, locale]);

  const hasAppliedFilters: boolean = useMemo( // Optionally add the explicit type here for clarity
    () => !!((queryParams as Partial<JobSearchQueryParams>)?.keyword || countAppliedFilters(queryParams) > 0),
    [queryParams],
  );

  // Geolocation permission state for controlling the "Use my location" action
  const geoPermission = useGeolocationPermission();

  // IP-based city label: prefer header-provided city name when available
  const [cityToDisplay, setCityToDisplay] = useState<string | null>(ipCityFromHeader ?? null);

  // Open the filters drawer and command the filters hub to open LocationForm
  const { openDrawer, setActiveSection } = useFilterDrawer();
  const openLocationFilter = () => {
    // Mark the Location section as active and then open the drawer
    setActiveSection('location');
    openDrawer();
  };

  // Resolve city name from coordinates via reverse geocoding
  // This ONLY runs when:
  // 1. User clicked "Use my location" (shouldLoadMaps === true)
  // 2. Maps library is loaded and ready (isMapsReady === true)
  // Maps are NEVER loaded automatically - only on user interaction
  useEffect(() => {
    // Maps are only loaded when user clicks "Use my location" button
    // If coordinates come from headers, we use ipCityFromHeader instead (no maps needed)
    if (!isMapsReady || !shouldLoadMaps) return;

    const latStr = queryParams?._lat;
    const lonStr = queryParams?._lon;
    const locArrLocal = (queryParams?.location ?? undefined) as unknown as string[] | undefined;
    const hasManualLocationLocal = Array.isArray(locArrLocal) && locArrLocal.length > 0;

    // If we already have a city from header or a manual location is set, skip reverse geocoding
    if (cityToDisplay || !latStr || !lonStr || hasManualLocationLocal) {
      return;
    }

    void (async () => {
      try {
        const lat = Number(latStr);
        const lng = Number(lonStr);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          const cached = getCachedCity(lat, lng, 3, undefined, locale);
          if (cached) {
            setCityToDisplay(cached);
          } else {
            const { getAddressComponentsFromCoordinates } = await import('@root/src/utils/google-maps');
            const comps = await getAddressComponentsFromCoordinates({ lat, lng });
            const city = comps?.find((c) => c.types.includes('locality'))?.long_name
              || comps?.find((c) => c.types.includes('administrative_area_level_1'))?.long_name
              || null;
            if (city) {
              setCityToDisplay(city);
              setCachedCity(lat, lng, city, 3, locale);
            }
          }
        }
      } catch {
        // ignore silently
      }
    })();
  }, [isMapsReady, shouldLoadMaps, cityToDisplay, queryParams?._lat, queryParams?._lon, queryParams?.location, locale, ipCityFromHeader]);

  // Ensure manual text location and coords don't conflict: if user sets a manual location, clear coords
  useEffect(() => {
    const locArr2 = (queryParams?.location ?? undefined) as unknown as string[] | undefined;
    const hasManualLocation2 = Array.isArray(locArr2) && locArr2.length > 0;
    const hasCoords = Boolean(queryParams?._lat || queryParams?._lon);

    if (hasManualLocation2 && hasCoords) {
      setCurrentPage('');
      setQueryParams((prev) => {
        if (prev && typeof prev === 'object') {
          const { _lat: omitLat, _lon: omitLon, ...rest } = prev;
          return rest;
        }
        return {} as JobSearchQueryParams;
      });
    }
  }, [queryParams?.location, queryParams?._lat, queryParams?._lon, setQueryParams, setCurrentPage]);

  // Safety: if only one of _lat/_lon is present, remove both to avoid malformed queries
  useEffect(() => {
    const hasLat = Boolean(queryParams?._lat);
    const hasLon = Boolean(queryParams?._lon);
    if (hasLat !== hasLon) {
      setQueryParams((prev) => {
        const { _lat: omittedLat, _lon: omittedLon, ...rest } = prev;
        return rest;
      });
    }
  }, [queryParams?._lat, queryParams?._lon, setQueryParams]);

  // Optional normalization: remove empty location arrays so we don't send an empty key
  useEffect(() => {
    const locArr3 = (queryParams?.location ?? undefined) as unknown as string[] | undefined;
    if (Array.isArray(locArr3) && locArr3.length === 0) {
      setQueryParams((prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const { location: omittedLocation, ...rest } = prev as JobSearchQueryParams;
        return rest;
      });
    }
  }, [queryParams?.location, setQueryParams]);

  const handleUseMyLocation = () => {
    if (!('geolocation' in navigator)) {
      bjToast.error(t('global.error.current_location.not_supported'));
      return;
    }

    // THIS IS THE ONLY PLACE WHERE MAPS ARE LOADED
    // Maps library is lazy-loaded only when user explicitly clicks "Use my location"
    // In all other cases (headers, etc.), we use ipCityFromHeader - no maps needed
    if (!shouldLoadMaps) {
      setShouldLoadMaps(true);
    }

    // Clear city from headers so we can show the geocoded city instead
    setCityToDisplay(null);

    setIsGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const cached = getCachedCity(latitude, longitude, 3, undefined, locale);

        // Remember last “Use my location” coords as well
        rememberLastCoords(latitude, longitude);

        // Update filters (this triggers data reload)
        setCurrentPage('');
        setQueryParams((prev) => {
          const { location: omitLocation, ...rest } = (prev || {}) as JobSearchQueryParams & Record<string, unknown>;
          return { ...(rest as typeof prev), _lat: String(latitude), _lon: String(longitude) };
        });

        setIsGettingLocation(false);

        // If we have a cached city, use it immediately
        // Otherwise, the main useEffect will handle geocoding once maps are ready
        if (cached) {
          setCityToDisplay(cached);
        }
      },
      (error) => {
        if (error.code === 1) {
          bjToast.error(t('global.error.current_location.permission_denied'));
        } else {
          bjToast.error(t('global.something.went.wrong'));
        }

        setIsGettingLocation(false);

        // If Permissions API is not available to auto-hide, persist a local flag
        try {
          if (error.code === 1) localStorage.setItem('geoPermDenied', '1');
        } catch { /* empty */ }
      },
      { enableHighAccuracy: false, timeout: 10000 },
    );
  };

  const handleMapsReady = useCallback(() => {
    setIsMapsReady(true);
  }, []);

  // Render page component
  // *****************************************************
  const pageContent = (
    <>
      {/* HEAD - title, meta, etc */}
      <Head>
        <title>{headTitle}</title>
        <meta name="description" content={metaDescription} />

        {/* Open Graph */}
        <meta property="og:url" content={ogUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={headTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="fb:app_id" content={facebookAppId} />
        <meta property="og:image" content={ogImageUrl} />
      </Head>

      {/* PAGE */}
      <div className="min-h-screen bg-surface-100 md:min-h-3/4-screen">

        {/* Filters */}
        <JobSearchFilters queryParams={queryParams} setQueryParams={setQueryParams} setFilterLabels={setFilterLabels} />

        <div className="container relative mt-5">
          {/* Breadcrumbs */}
          {breadcrumbs && hasAppliedFilters && <Breadcrumbs className="mb-1" breadcrumbs={breadcrumbs} />}

          <JobListBanners
            isAuthenticated={isAuthenticated}
            hasAppliedFilters={hasAppliedFilters}
            userOwnResumeFromServer={userOwnResumeFromServer}
            locale={locale}
          />

          {!hasManualLocation && (
            <div className="mb-3 inline-flex items-center gap-2 text-sm text-ink">
              {cityToDisplay && (
                <button
                  type="button"
                  onClick={openLocationFilter}
                  data-test-id="job-list-location"
                  className={clsx(
                    'inline-flex min-w-0 items-center gap-2',
                    'text-ink hover:text-primary',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                    'underline-offset-2 hover:underline',
                  )}
                  aria-label={t('global.location.label')}
                >
                  <MapPinIcon aria-hidden="true" className="size-4 text-ink-light" />
                  <strong className="truncate font-semibold">{cityToDisplay}</strong>
                </button>
              )}

              {geoPermission !== 'denied' && (
                <Button
                  size="xs"
                  rounding="full"
                  onClick={handleUseMyLocation}
                  data-test-id="use-my-location-button"
                  isLoading={isGettingLocation}
                  spinnerClass="w-3 h-3 -ml-0.5 mr-2"
                  disabled={isGettingLocation}
                  className={clsx(
                    'shrink-0 !bg-transparent !shadow-none',
                    'border border-primary !text-primary',
                    'hover:bg-primary/5',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                  )}
                  aria-label={t('job.location.use_my_location')}
                >
                  {/* Target-like icon */}
                  <svg
                    aria-hidden="true"
                    className="-ml-0.5 mr-2 size-4 text-primary"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M10 2a1 1 0 0 1 1 1v1.06a6.94 6.94 0 0 1 4.94 4.94H17a1 1 0 1 1 0 2h-1.06A6.94 6.94 0 0 1 11 15.94V17a1 1 0 1 1-2 0v-1.06A6.94 6.94 0  0 1 4.06 11H3a1 1 0 1 1 0-2h1.06A6.94 6.94 0 0 1 9 4.06V3a1 1 0 0 1 1-1Zm0 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />
                  </svg>
                  {t('job.location.use_my_location')}
                </Button>
              )}
            </div>
          )}

          {/* Total items + Save job search */}
          <div className="mb-5 flex flex-col items-start justify-between gap-4 sm:flex-row sm:gap-0">
            {searchId && (
              <Button
                rounding="full"
                size="xs"
                className={clsx('shrink-0 !bg-surface-100 !text-sm !text-pink hover:!bg-pink/10 hover:!shadow-none', isSavedJobSearchAlert && 'pointer-events-none')}
                isLoading={isCreatingJobSearchAlert}
                spinnerClass="w-4 h-4 -ml-1 mr-2"
                onClick={createJobSearchAlert}
              >
                {!isCreatingJobSearchAlert && <BellIcon className="-ml-1 mr-2 size-4" />}
                { isSavedJobSearchAlert ? t('saved.job.alert') : t('save.job.alert') }
              </Button>
            )}
          </div>

          {/* Infinite list */}
          <JobCardList
            data={data}
            trackingData={{
              listType: 'search',
              listUuid: apiRequestId,
            }}
            t={t}
            setCurrentPage={setCurrentPage as Dispatch<SetStateAction<string>>}
            isLoading={isLoading}
            size={size}
            setSize={setSize}
            sizeAsRef={sizeAsRef}
            showLoadMore={showLoadMore}
            isEmpty={isEmpty}
            isValidating={isValidating}
            listType={JobListType.SEARCH_LIST_APISEARCH}
            locale={locale}
            renderBanner={(jobPosition) => {
              const showJobAlert = searchId && JOB_ALERT_BANNER_POSITIONS.includes(jobPosition as typeof JOB_ALERT_BANNER_POSITIONS[number]);

              if (!showJobAlert) return null;

              return (
                <div className="col-span-1 md:col-span-2 lg:col-span-3">
                  {showJobAlert && (
                    <JobAlertCard
                      createFn={createJobSearchAlert}
                      isSaved={isSavedJobSearchAlert}
                      isSaving={isCreatingJobSearchAlert}
                    />
                  )}
                </div>
              );
            }}
            title={title}
            paginationType="cursor"
          />

          {/* SEO Widget - Rendered only Server-Side - no need to update in client */}
          {seoData && (seoData.seoCities || seoData.seoDomains) && (
            <JobSeoWidget
              seoCities={seoData.seoCities}
              seoDomains={seoData.seoDomains}
              seoIsAbroad={seoData.seoIsAbroad}
            />
          )}

          {/* Loading Blurred Overlay */}
          {showLoadingOverlay && (
            <div className="absolute inset-0 z-2 cursor-wait backdrop-blur-none lg:backdrop-blur-xs" />
          )}
        </div>

      </div>
    </>
  );


  return (
    <>
      {pageContent}
      {shouldLoadMaps && (
        <GoogleMapsProvider apiKey={googleAPIKey} language={locale} onReady={handleMapsReady} />
      )}
    </>
  );
};

// Page layout; Leverage the HeaderSearchContext.Provider in order to pass the
// query params to the search input component.
// ************************************************************************************
JobListPage.getLayout = function getLayout(page: ReactNode) {
  return (
    <FilterDrawerProvider>
      <LayoutDefault searchContext={headerSearchContextValue}>
        {page}
      </LayoutDefault>
    </FilterDrawerProvider>
  );
};

// GSSP API
// query and params are needed for the initial request used by the infinite scrolling
// ************************************************************************************
export const getServerSideProps: GetServerSideProps<JobListPageProps> = async (context) => {
  // Destructure context
  const {
    req,
    query,
  } = context;

  const locale = gsspGetLocale(query);

  // Server side translations
  const { t } = await getServerSideTranslation(locale, 'common');

  // Process the query to get the search params
  // query is like: { slug: [ 'full-time', 'beneficii', 'bonus-de-performanta', 'germana' ] }
  const {
    slug,
  } = query;

  const filterParams = getJobListParametersFromSlug(slug, t);

  // Process the query for the infinite scrolling. Use the returned object to pass parameters
  // to useSwrTalentSearchV2 hook and filters.
  const infiniteQueryParams = paginatedServerSideQuery({
    query,
    pageSlug: 'cursor',
    itemsPerPage: JOB_LIST_ITEMS_PER_PAGE,
    paginationType: 'cursor',
  });

  // Add location fallback from loadbalancer header if not already present
  const locationFromHeader = parseLocationHeader(req.headers['x-client-citylatlong'] as string);
  if (locationFromHeader && !filterParams._lat && !filterParams._lon && !query._lat && !query._lon) {
    filterParams._lat = locationFromHeader._lat;
    filterParams._lon = locationFromHeader._lon;
  }

  infiniteQueryParams.objFilterParams = filterParams as ObjectFilterParams;
  infiniteQueryParams.stringFilterParams = buildSearchParams(filterParams, '');

  infiniteQueryParams.fetchUrlParams += buildSearchParams(filterParams, '&');

  // Check if the user is authenticated
  // *****************************************
  const isAuthenticated = gsspCheckCookieAuth({ req }) as boolean;

  // Fetch data from server
  // *****************************************
  const [
    jobListCardsFromServer,
    breadcrumbsFromServer,
    seoDataFromServer,
    userOwnResumeFromServer,
  ] = await Promise.all([

    fetcher<ApiJobListCards>(`/v2/jobs${infiniteQueryParams.fetchUrlParams}`, locale, {
      ssrClientHeaders: req.headers,
    }),
    !isAuthenticated ? fetcher<ApiBreadcrumbs>(`/v1/jobs/breadcrumb${infiniteQueryParams.fetchUrlParams}`, locale, {
      ssrClientHeaders: req.headers,
    }) : Promise.resolve(),
    fetcher<ApiSchema<'SeoData'>>(`/v2/jobs/seo-data${infiniteQueryParams.fetchUrlParams}`, locale, {
      ssrClientHeaders: req.headers,
    }),
    isAuthenticated
      ? fetcher<ApiOwnResumeDetail>('/v1/own-resume-detail', locale, { ssrClientHeaders: req.headers })
      : Promise.resolve(),
  ]);

  // Return props
  // *********************************
  // Extract IP-detected city name from header for banner display
  const ipCityFromHeaderRaw = context.req.headers['x-client-city'] as string | undefined;
  const ipCityFromHeader = typeof ipCityFromHeaderRaw === 'string'
    ? (ipCityFromHeaderRaw.split(',')[0].trim() || null)
    : null;

  return ({
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      locale,
      infiniteQueryParams,
      jobListCardsFromServer: isJobListCardsOk(jobListCardsFromServer) ? jobListCardsFromServer : null,
      breadcrumbsFromServer: isBreadcrumbsOk(breadcrumbsFromServer) ? breadcrumbsFromServer : null,
      seoDataFromServer: seoDataFromServer || null,
      isAuthenticated,
      userOwnResumeFromServer: isOwnResumeDetailOk(userOwnResumeFromServer) ? userOwnResumeFromServer : null,
      ipCityFromHeader,
    },
  });
};

// Page export
// ************************************************************************************
export default JobListPage;
